import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Typography, Tag, Button, Space, Spin, Collapse, Table,
} from 'antd';
import {
  AppstoreOutlined, TagsOutlined, PictureOutlined, DollarOutlined,
  DatabaseOutlined, SettingOutlined, BarcodeOutlined, EditOutlined,
  ArrowLeftOutlined, InboxOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import { parseUomUnits, resolveCasePrice, getCaseQuantity } from '../../utils/wholesaleProduct';
import './ProductList.css';
import './Customerview.css';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const BLUE   = '#1890ff';
const GREEN  = '#10b981';
const GREY   = '#6b7280';
const PURPLE = '#8b5cf6';

const cardStyle: React.CSSProperties = {
  borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 20,
};
const sectionHead: React.CSSProperties = {
  fontSize: 15, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: 8,
};
const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block',
};
const valueStyle: React.CSSProperties = {
  fontSize: 15, color: '#111827', fontWeight: 500,
};

const ProductView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    apiFetch(`${API_URL}/products/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setProduct(d))
      .catch(err => { console.error(err); setProduct(null); })
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '80px 0' }}>
      <Spin size="large" />
    </div>
  );

  if (!product) return (
    <div style={{ textAlign: 'center', padding: '80px 0' }}>
      <Text type="secondary" style={{ fontSize: 16 }}>Product not found</Text>
      <br /><br />
      <Button onClick={() => navigate('/products/list')}>← Back to Products</Button>
    </div>
  );

  const cost   = Number(product.cost  || 0);
  const price  = Number(product.price || 0);
  const profit = price - cost;
  const margin = price > 0 ? (profit / price) * 100 : 0;
  const markup = cost  > 0 ? (profit / cost)  * 100 : 0;
  const stock  = Number(product.quantity || product.stock || 0);
  const reorderPt = Number(product.reorderPoint ?? product.reorder_point ?? product.minStock ?? product.min_stock ?? 10);
  const isLowStock = stock <= reorderPt;
  const wholesalePrice = product.wholesale_price ? Number(product.wholesale_price) : null;
  const boxQty = product.box_quantity ? Number(product.box_quantity) : null;
  const caseQty = getCaseQuantity(product) || boxQty;
  const casePriceVal = product.case_price ? Number(product.case_price) : resolveCasePrice(product, wholesalePrice || price, { isWholesale: true });
  const tierA = product.tier_a_price != null ? Number(product.tier_a_price) : null;
  const tierB = product.tier_b_price != null ? Number(product.tier_b_price) : null;
  const tierC = product.tier_c_price != null ? Number(product.tier_c_price) : null;
  const uomUnits = parseUomUnits(product.uom_units);
  const netWeight = product.net_weight || null;
  const grossWeight = product.gross_weight || null;
  const countryOfOrigin = product.country_of_origin || null;
  const eanBarcode = product.barcode || null;
  const boxesPerPallet = product.boxes_per_pallet ? Number(product.boxes_per_pallet) : null;
  const minOrderQty = product.min_order_qty ? Number(product.min_order_qty) : null;
  const isPublicShop = product.is_public_shop !== false && product.is_public_shop !== 'false';
  const stockInBoxes = boxQty && boxQty > 0 ? Math.floor(stock / boxQty) : null;

  const productType: string = product.productType || product.product_type || 'Physical';
  const tags: string[] = product.tags
    ? (Array.isArray(product.tags) ? product.tags : String(product.tags).split(',').filter(Boolean))
    : [];
  const variants: any[] = product.variants
    ? (Array.isArray(product.variants) ? product.variants : (() => { try { return JSON.parse(product.variants); } catch { return []; } })())
    : [];

  const statusColor: Record<string, string> = {
    Active: 'green', Draft: 'blue', Inactive: 'default',
    'Out of Stock': 'orange', Discontinued: 'red',
  };

  const typeIcon: Record<string, string> = {
    Physical: '📦', Service: '🛠️', Digital: '💾', Variant: '🎨',
  };

  const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div style={{ marginBottom: 18 }}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value ?? <Text type="secondary">—</Text>}</span>
    </div>
  );

  const StatBox = ({ label, value, color }: { label: string; value: string; color: string }) => (
    <div style={{ textAlign: 'center', flex: 1, padding: '18px 8px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb' }}>
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 980, margin: '28px auto', padding: '0 20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>{product.name}</Title>
          <Space style={{ marginTop: 6 }}>
            <Tag color={statusColor[product.status] || 'default'}>{product.status || 'Active'}</Tag>
            <Text type="secondary" style={{ fontSize: 13 }}>{typeIcon[productType]} {productType}</Text>
            {isLowStock && <Tag color="red">⚠ Low Stock</Tag>}
          </Space>
        </div>
        <div className="cv-actions">
          <button type="button" className="cv-btn cv-btn--back" onClick={() => navigate('/products/list')}>Back</button>
          <button type="button" className="cv-btn cv-btn--edit" onClick={() => navigate(`/products/edit/${id}`)}>✏️ Edit</button>
        </div>
      </div>

      {/* 1. Product Type */}
      <Card title={<span style={sectionHead}><AppstoreOutlined style={{ color: BLUE }} /> Product Type</span>} style={cardStyle}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14, padding: '14px 22px', background: '#eff6ff', borderRadius: 10, border: `2px solid ${BLUE}` }}>
          <span style={{ fontSize: 32 }}>{typeIcon[productType]}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: BLUE }}>{productType}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {{ Physical: 'Physical goods with inventory', Service: 'Services, labour, consulting', Digital: 'Downloadable files, software', Variant: 'Multiple sizes, colours, etc.' }[productType]}
            </div>
          </div>
        </div>
      </Card>

      {/* 2. Basic Info */}
      <Card title={<span style={sectionHead}><TagsOutlined style={{ color: BLUE }} /> Basic Information</span>} style={cardStyle}>
        <Row gutter={24}>
          <Col xs={24} sm={16}>
            <Field label="Product Name" value={product.name} />
          </Col>
          <Col xs={24} sm={8}>
            <Field label="Status" value={<Tag color={statusColor[product.status] || 'default'}>{product.status || 'Active'}</Tag>} />
          </Col>
        </Row>
        {product.description ? (
          <div style={{ marginBottom: 18 }}>
            <span style={labelStyle}>Description</span>
            <div style={{ fontSize: 14, color: '#374151', background: '#f9fafb', padding: '12px 16px', borderRadius: 8, lineHeight: 1.7 }}>
              {product.description}
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 18 }}>
            <span style={labelStyle}>Description</span>
            <Text type="secondary">No description — add one when editing this product.</Text>
          </div>
        )}
        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Field label="SKU" value={<code style={{ background: '#f3f4f6', padding: '2px 8px', borderRadius: 4, fontSize: 13 }}>{product.sku || '—'}</code>} />
          </Col>
          <Col xs={24} sm={12}>
            <Field label="Tags" value={
              tags.length
                ? <Space wrap>{tags.map(t => <Tag key={t} color="blue">{t}</Tag>)}</Space>
                : <Text type="secondary">—</Text>
            } />
          </Col>
        </Row>
      </Card>

      {/* 3. Food Safety & Allergens — matches Add/Edit form (section 2) */}
      {(() => {
        const expiryRaw = product.expiry_date || product.expiryDate;
        const allergenStr = product.allergens;
        const dietaryStr  = product.dietary_info;
        const ingredientsStr = product.ingredients;

        const allergenList: string[] = allergenStr ? String(allergenStr).split(',').filter(Boolean) : [];
        const dietaryList: string[]  = dietaryStr  ? String(dietaryStr).split(',').filter(Boolean)  : [];

        let expiryLabel = 'Not set';
        let expiryColor = '#6b7280';
        let expiryBg    = '#f9fafb';
        let expiryBorder = '#e5e7eb';
        if (expiryRaw) {
          const expiry = new Date(expiryRaw);
          const today  = new Date();
          const diffDays = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          expiryLabel = expiry.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
          if (diffDays < 0)  { expiryColor = '#dc2626'; expiryBg = '#fef2f2'; expiryBorder = '#fca5a5'; }
          else if (diffDays <= 30) { expiryColor = '#d97706'; expiryBg = '#fff7ed'; expiryBorder = '#fcd34d'; }
          else { expiryColor = '#166534'; expiryBg = '#f0fdf4'; expiryBorder = '#86efac'; }
        }

        return (
          <Card
            title={<span style={sectionHead}><span style={{ fontSize: 15 }}>🛡️</span> Food Safety &amp; Allergens</span>}
            style={cardStyle}
          >
            <div style={{ marginBottom: 20, padding: '14px 18px', background: expiryBg, border: `1.5px solid ${expiryBorder}`, borderRadius: 10 }}>
              <span style={labelStyle}>Expiry / Best Before</span>
              <span style={{ ...valueStyle, color: expiryColor, fontWeight: 700, fontSize: 17 }}>
                {expiryLabel}
                {expiryRaw && (() => {
                  const expiry = new Date(expiryRaw);
                  const today  = new Date();
                  const diffDays = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                  if (diffDays < 0)     return <Tag color="red"   style={{ marginLeft: 10 }}>EXPIRED</Tag>;
                  if (diffDays <= 7)    return <Tag color="orange" style={{ marginLeft: 10 }}>Expires in {diffDays} day{diffDays !== 1 ? 's' : ''}</Tag>;
                  if (diffDays <= 30)   return <Tag color="gold"   style={{ marginLeft: 10 }}>Expiring — {diffDays} days</Tag>;
                  return null;
                })()}
              </span>
            </div>

            {allergenList.length > 0 ? (
              <div style={{ marginBottom: 20 }}>
                <span style={labelStyle}>⚠️ Contains Allergens (UK FIC)</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {allergenList.map(a => (
                    <Tag key={a} color="red" style={{ fontSize: 13, padding: '4px 12px', fontWeight: 600 }}>{a}</Tag>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 20 }}>
                <span style={labelStyle}>Allergens</span>
                <Text type="secondary">None recorded</Text>
              </div>
            )}

            {dietaryList.length > 0 ? (
              <div style={{ marginBottom: ingredientsStr ? 20 : 0 }}>
                <span style={labelStyle}>Dietary Suitability</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {dietaryList.map(d => (
                    <Tag key={d} color="green" style={{ fontSize: 13, padding: '4px 12px', fontWeight: 600 }}>{d}</Tag>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 20 }}>
                <span style={labelStyle}>Dietary suitability</span>
                <Text type="secondary">None recorded</Text>
              </div>
            )}

            {ingredientsStr ? (
              <div>
                <span style={labelStyle}>Ingredients</span>
                <div style={{ fontSize: 14, color: '#374151', background: '#f9fafb', padding: '12px 16px', borderRadius: 8, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {ingredientsStr}
                </div>
              </div>
            ) : (
              <div>
                <span style={labelStyle}>Ingredients</span>
                <Text type="secondary">Not provided</Text>
              </div>
            )}
          </Card>
        );
      })()}

      {/* 4. Images */}
      {(product.image1 || product.image2 || product.image3 || product.image) && (
        <Card title={<span style={sectionHead}><PictureOutlined style={{ color: BLUE }} /> Images</span>} style={cardStyle}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {[product.image1 || product.image, product.image2, product.image3].filter(Boolean).map((url: string, i: number) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={url} alt={`Product ${i + 1}`}
                  style={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 10, border: '2px solid #e5e7eb', display: 'block' }} />
                {i === 0 && (
                  <div style={{ position: 'absolute', bottom: 8, left: 8, background: BLUE, color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>MAIN</div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 5. Pricing */}
      <Card title={<span style={sectionHead}><DollarOutlined style={{ color: BLUE }} /> Pricing</span>} style={cardStyle}>

        {/* ── Retail Pricing ── */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>🛒 Retail Pricing</div>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Field label="Cost Price" value={`£${cost.toFixed(2)}`} />
            </Col>
            <Col xs={24} sm={8}>
              <Field label="Retail Unit Price" value={<span style={{ color: GREEN, fontWeight: 700, fontSize: 18 }}>£{price.toFixed(2)}</span>} />
            </Col>
            <Col xs={24} sm={8}>
              {boxQty ? (
                <Field
                  label={`Retail Box Price (×${boxQty} units)`}
                  value={<span style={{ fontWeight: 700, fontSize: 16, color: GREEN }}>£{(price * boxQty).toFixed(2)}</span>}
                />
              ) : (
                <Field label="Sale Price" value={product.salePrice || product.sale_price ? `£${Number(product.salePrice || product.sale_price).toFixed(2)}` : null} />
              )}
            </Col>
          </Row>
        </div>

        {/* ── Wholesale / B2B Pricing ── */}
        {(wholesalePrice || casePriceVal || tierA || tierB || tierC) && (
          <div style={{ margin: '0 0 16px', padding: '14px 18px', background: '#f0fdf4', borderRadius: 10, border: '1.5px solid #86efac' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>📦 Wholesale Trade Engine</div>
            <Row gutter={[16, 12]}>
              {wholesalePrice && (
                <Col xs={12} sm={6}>
                  <Field label="Wholesale Unit Price" value={<span style={{ color: '#15803d', fontWeight: 700, fontSize: 17 }}>£{wholesalePrice.toFixed(2)}</span>} />
                </Col>
              )}
              {caseQty && (
                <Col xs={12} sm={6}>
                  <Field label="Units per Case / Outer" value={<span style={{ fontWeight: 600 }}>{caseQty} units</span>} />
                </Col>
              )}
              {casePriceVal != null && (
                <Col xs={12} sm={6}>
                  <Field label="Case / Outer Price" value={<span style={{ fontWeight: 800, fontSize: 16, color: '#15803d' }}>£{casePriceVal.toFixed(2)}</span>} />
                </Col>
              )}
              {tierA != null && tierA > 0 && (
                <Col xs={12} sm={6}><Field label="Tier A Price" value={`£${tierA.toFixed(2)}`} /></Col>
              )}
              {tierB != null && tierB > 0 && (
                <Col xs={12} sm={6}><Field label="Tier B Price" value={`£${tierB.toFixed(2)}`} /></Col>
              )}
              {tierC != null && tierC > 0 && (
                <Col xs={12} sm={6}><Field label="Tier C Price" value={`£${tierC.toFixed(2)}`} /></Col>
              )}
              {boxesPerPallet && boxQty && wholesalePrice && (
                <Col xs={12} sm={6}>
                  <Field
                    label={`Pallet Price (×${boxesPerPallet} boxes)`}
                    value={<span style={{ fontWeight: 800, fontSize: 16, color: '#15803d' }}>£{(wholesalePrice * boxQty * boxesPerPallet).toFixed(2)}</span>}
                  />
                </Col>
              )}
              {boxesPerPallet && boxQty && (
                <Col xs={12} sm={6}>
                  <Field label="Units per Pallet" value={<span style={{ fontWeight: 600 }}>{boxQty * boxesPerPallet} units ({boxesPerPallet} boxes)</span>} />
                </Col>
              )}
              {minOrderQty && (
                <Col xs={12} sm={6}>
                  <Field label="Min. Order Qty" value={`${minOrderQty} units`} />
                </Col>
              )}
            </Row>
          </div>
        )}

        {uomUnits.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8 }}>UNITS OF MEASURE</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ textAlign: 'left', padding: 8 }}>UOM</th>
                  <th style={{ padding: 8 }}>= Base units</th>
                  <th style={{ padding: 8 }}>Price</th>
                  <th style={{ padding: 8 }}>Barcode</th>
                </tr>
              </thead>
              <tbody>
                {uomUnits.map(u => (
                  <tr key={u.code} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: 8 }}>{u.label}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>{u.factor}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>{u.price != null ? `£${Number(u.price).toFixed(2)}` : 'Auto'}</td>
                    <td style={{ padding: 8, textAlign: 'center', fontFamily: 'monospace' }}>{u.barcode || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(eanBarcode || netWeight || grossWeight || countryOfOrigin) && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            {eanBarcode && <Col xs={24} sm={8}><Field label="EAN-13 Barcode" value={<span style={{ fontFamily: 'monospace' }}>{eanBarcode}</span>} /></Col>}
            {netWeight && <Col xs={24} sm={8}><Field label="Net Weight" value={netWeight} /></Col>}
            {grossWeight && <Col xs={24} sm={8}><Field label="Gross Weight" value={grossWeight} /></Col>}
            {countryOfOrigin && <Col xs={24} sm={8}><Field label="Country of Origin" value={countryOfOrigin} /></Col>}
          </Row>
        )}

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}><Field label="Tax Rate" value={`${product.taxRate ?? product.tax_rate ?? 20}%`} /></Col>
        </Row>

        {/* Profit / margin / markup stat boxes */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <StatBox label="Profit" value={`£${profit.toFixed(2)}`} color={profit >= 0 ? GREEN : '#ef4444'} />
          <StatBox label="Margin" value={`${margin.toFixed(1)}%`} color={margin >= 0 ? GREEN : '#ef4444'} />
          <StatBox label="Markup" value={`${markup.toFixed(1)}%`} color={markup >= 0 ? BLUE : '#ef4444'} />
          <StatBox label="Stock Value" value={`£${(price * stock).toFixed(2)}`} color={PURPLE} />
        </div>
      </Card>

      {/* Online Shop Status */}
      <Card style={{ ...cardStyle, background: isPublicShop ? '#f0fdf4' : '#fafafa', border: isPublicShop ? '1.5px solid #86efac' : '1.5px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 28 }}>{isPublicShop ? '🛍️' : '🔒'}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: isPublicShop ? '#166534' : '#374151' }}>
              {isPublicShop ? 'Visible in Online Shop' : 'Hidden from Online Shop'}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {isPublicShop ? 'Customers can find and buy this product.' : 'This product is not listed publicly.'}
            </div>
          </div>
          <Tag color={isPublicShop ? 'green' : 'default'} style={{ marginLeft: 'auto', fontWeight: 700 }}>
            {isPublicShop ? 'PUBLIC' : 'PRIVATE'}
          </Tag>
        </div>
      </Card>

      {/* 6. Inventory */}
      {productType !== 'Service' && productType !== 'Digital' && (
        <Card
          title={<span style={sectionHead}><DatabaseOutlined style={{ color: BLUE }} /> Inventory</span>}
          extra={isLowStock ? <Tag color="red">⚠ Low Stock</Tag> : <Tag color="green">Stock OK</Tag>}
          style={cardStyle}
        >
          {(product.trackInventory === 'false' || product.trackInventory === false) ? (
            <Text type="secondary">Inventory tracking is off for this product</Text>
          ) : (
            <Row gutter={24}>
              <Col xs={12} sm={6}>
                <Field label="Current Stock" value={
                  <span>
                    <span style={{ fontSize: 22, fontWeight: 800, color: isLowStock ? '#ef4444' : GREEN }}>{stock}</span>
                    <span style={{ fontSize: 13, color: '#6b7280', marginLeft: 4 }}>{product.unit || 'units'}</span>
                    {stockInBoxes !== null && (
                      <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
                        = {stockInBoxes} box{stockInBoxes !== 1 ? 'es' : ''} of {boxQty}
                      </div>
                    )}
                  </span>
                } />
              </Col>
              <Col xs={12} sm={6}><Field label="Unit" value={product.unit || 'pcs'} /></Col>
              <Col xs={12} sm={6}><Field label="Reorder Point" value={reorderPt} /></Col>
              <Col xs={12} sm={6}><Field label="Low Stock Alert" value={product.lowStockAlert === 'true' || product.lowStockAlert === true ? <Tag color="orange">Email On</Tag> : <Tag>Off</Tag>} /></Col>
            </Row>
          )}
        </Card>
      )}

      {/* 7. Organisation */}
      <Card title={<span style={sectionHead}><SettingOutlined style={{ color: BLUE }} /> Organisation</span>} style={cardStyle}>
        <Row gutter={24}>
          <Col xs={24} sm={8}><Field label="Category" value={product.category || null} /></Col>
          <Col xs={24} sm={8}><Field label="Supplier" value={product.supplier_name || product.supplier || null} /></Col>
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 18 }}>
              <span style={labelStyle}>Brand / Manufacturer <span style={{ textTransform: 'none', fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>(internal, optional)</span></span>
              <span style={valueStyle}>{product.brand || <Text type="secondary">—</Text>}</span>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 7. Variants */}
      {productType === 'Variant' && variants.length > 0 && (
        <Card title={<span style={sectionHead}>Variant Options</span>} style={cardStyle}>
          <Table size="small" dataSource={variants} rowKey="key" pagination={false}
            style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}
            columns={[
              { title: 'Variant Name', dataIndex: 'name' },
              { title: 'SKU',          dataIndex: 'sku'  },
              { title: 'Price (£)',    dataIndex: 'price', render: (v: string) => v ? `£${Number(v).toFixed(2)}` : '—' },
              { title: 'Stock',        dataIndex: 'stock', render: (v: string) => v || '—' },
            ]}
          />
        </Card>
      )}

      {/* QR Code */}
      {(product.sku || product.name) && (() => {
        const qrPayload = JSON.stringify({ sku: product.sku || '', name: product.name || '' });
        const downloadQR = () => {
          const svg = document.querySelector('.product-view-qr svg') as SVGElement;
          if (!svg) return;
          const blob = new Blob([svg.outerHTML], { type: 'image/svg+xml' });
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = `${product.sku || product.name}-qr.svg`;
          a.click();
        };
        return (
          <Card
            title={<span style={sectionHead}><BarcodeOutlined style={{ color: '#8b5cf6' }} /> QR Code</span>}
            style={cardStyle}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 28, flexWrap: 'wrap' }}>
              <div className="product-view-qr" style={{ background: '#fff', padding: 16, borderRadius: 10, border: '1.5px solid #e5e7eb', display: 'inline-block' }}>
                <QRCodeSVG value={qrPayload} size={160} level="M" includeMargin />
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <p style={{ margin: '0 0 6px', fontSize: 14, color: '#374151' }}>
                  This QR code encodes the <strong>product SKU</strong> and <strong>name</strong>. Scan it with any QR reader or print it and stick it on the product/shelf.
                </p>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
                  Payload: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>{qrPayload}</code>
                </p>
                <Space wrap>
                  <Button icon={<BarcodeOutlined />} onClick={downloadQR}>Download SVG</Button>
                  <Button onClick={() => {
                    const win = window.open('', '_blank');
                    const svg = document.querySelector('.product-view-qr svg');
                    if (win && svg) {
                      win.document.write(`<html><body style="margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh">${svg.outerHTML}</body></html>`);
                      win.document.close();
                      win.print();
                    }
                  }}>Print</Button>
                </Space>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* 8. Digital File */}
      {productType === 'Digital' && (product.digitalFile || product.digital_file) && (
        <Card title={<span style={sectionHead}>Digital File</span>} style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac' }}>
            <InboxOutlined style={{ fontSize: 28, color: GREEN }} />
            <div>
              <div style={{ fontWeight: 600, color: '#111827' }}>{product.digitalFile || product.digital_file}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Downloadable digital product</div>
            </div>
          </div>
        </Card>
      )}

      {/* 9. Advanced */}
      <Collapse ghost style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 20 }}>
        <Panel header={<span style={{ fontWeight: 600, color: GREY }}>Advanced Details</span>} key="advanced">
          <Row gutter={24}>
            <Col xs={24} sm={8}>
              <Field label="Barcode" value={product.barcode ? <code style={{ background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>{product.barcode}</code> : null} />
            </Col>
            {productType === 'Physical' && (
              <>
                <Col xs={24} sm={8}><Field label="Weight" value={product.weight || null} /></Col>
                <Col xs={24} sm={8}><Field label="Dimensions" value={product.dimensions || null} /></Col>
              </>
            )}
          </Row>
          {product.notes ? (
            <div>
              <span style={labelStyle}>Internal Notes</span>
              <div style={{ fontSize: 14, color: '#374151', background: '#f9fafb', padding: '12px 16px', borderRadius: 8, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {product.notes}
              </div>
            </div>
          ) : (
            <div>
              <span style={labelStyle}>Internal Notes</span>
              <Text type="secondary">No internal notes</Text>
            </div>
          )}
        </Panel>
      </Collapse>

      {/* Record Info */}
      <Card style={{ ...cardStyle, background: '#f9fafb' }}>
        <Row gutter={16}>
          <Col xs={24} sm={8}><Field label="Product ID" value={<code style={{ fontSize: 12, color: '#6b7280' }}>{product._id || product.id}</code>} /></Col>
          <Col xs={24} sm={8}><Field label="Created" value={product.createdAt ? new Date(product.createdAt).toLocaleString() : null} /></Col>
          <Col xs={24} sm={8}><Field label="Last Updated" value={product.updatedAt ? new Date(product.updatedAt).toLocaleString() : null} /></Col>
        </Row>
      </Card>

      {/* Footer Actions */}
      <Card style={{ ...cardStyle, background: '#f9fafb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Button onClick={() => navigate('/products/list')}
            style={{ padding: '0 28px', height: 42, borderRadius: 8, fontWeight: 600, background: GREY, borderColor: GREY, color: '#fff' }}>
            Back to Products
          </Button>
          <Space wrap>
            <Button danger style={{ height: 42, borderRadius: 8, fontWeight: 600, padding: '0 28px' }}
              onClick={() => {
                if (!window.confirm(`Archive "${product.name}"? You can restore it from the product list.`)) return;
                apiFetch(`${API_URL}/products/${id}/archive`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } })
                  .then(r => { if (r.ok) { showToast('Product archived'); navigate('/products/list'); } else showToast('Failed to archive'); });
              }}>
              Archive Product
            </Button>
            <Button type="primary" icon={<EditOutlined />}
              style={{ height: 42, borderRadius: 8, fontWeight: 600, padding: '0 28px', background: GREEN, borderColor: GREEN }}
              onClick={() => navigate(`/products/edit/${id}`)}>
              Edit Product
            </Button>
          </Space>
        </div>
      </Card>

    </div>
  );
};

export default ProductView;
